// frontend/src/components/reports/vocabulario.ts
//
// COMPATIBILIDAD TEMPORAL. Este fichero existe para poder desplegar en dos
// tiempos y se borra en cuanto las vistas estén en inglés.
//
// Las vistas de Fabric emitían sus valores en castellano —`CABECERA`, `GRANEL`,
// `FRAGANCIA`— y pasan a emitirlos en inglés. Un VALOR no se migra como una
// columna: el viejo y el nuevo no pueden convivir en la misma fila, así que no
// vale el orden de siempre (backend → frontend → vista). La secuencia es:
//
//   1. este normalizador entra, y el registro y los rótulos pasan al vocabulario
//      inglés: desde ese momento TODA esta capa habla inglés, aunque las vistas
//      sigan en castellano;
//   2. se despliegan las vistas en inglés y el normalizador queda en no-op;
//   3. se borra este fichero y su uso.
//
// La alternativa —aceptar los dos valores en cada comparación— deja el paso 3
// convertido en una cacería por todo el código. Aquí los dos vocabularios se
// tocan en UN sitio, y borrarlo es mecánico.
//
// Lo que NO se traduce, y no es un olvido: `Disponible`, `PenCalidad`,
// `RECHAZADO`, `COMPRA INTERNA`, `ESENCIA ADULTO`, `STANDARD`, `ARMAF`… son
// valores del maestro de 365FO que se usan como criterio de cruce. Traducirlos
// rompería los joins SIN dar error: saldrían ceros.
import type { AnyReportRow } from '@/types'

/**
 * Valor viejo → valor nuevo, por columna.
 *
 * `tipo_fila` va en un solo mapa para los cuatro informes porque no hay
 * colisiones: `CABECERA` es `HEADER` en materia prima y en producto terminado, y
 * `COMPONENTE` es `COMPONENT` en cobertura y en consumo. El `FG` de cobertura no
 * cambia y por eso no está.
 */
const LEGADO: Record<string, Record<string, string>> = {
  tipoFila: {
    CABECERA: 'HEADER',
    LOTE: 'BATCH',
    FACTURA: 'INVOICE',
    OP: 'WORK_ORDER',
    COMPONENTE: 'COMPONENT',
    TERMINADO: 'FINISHED_GOOD',
    GRANEL: 'BULK',
  },
  // Guion bajo donde el valor es identificador —viaja en la URL como `?nivel=`—
  // y espacio donde se imprime: `seccion` sale en la columna SECTION del Excel.
  seccion: {
    TERMINADO: 'FINISHED GOOD',
    SEMIELABORADO: 'SEMI-FINISHED',
  },
  // `OTROS` no aparece en el dato (cero filas): es la red del CASE por si el
  // maestro trae una subfamilia nueva. Se mapea igual, que para eso está.
  tipoComponente: {
    FRAGANCIA: 'FRAGRANCE',
    DISOLVENTE: 'SOLVENT',
    INGREDIENTE: 'INGREDIENT',
    OTROS: 'OTHER',
  },
  tipoArticulo: {
    PRODUCTO: 'PRODUCT',
    GRANEL: 'BULK',
    VARIANTE: 'VARIANT',
  },
  // Se conservan las palabras que el cliente YA tiene en su Excel, que no son
  // una traducción literal del castellano: cambiarle el vocabulario a un fichero
  // entregado sin explicárselo se lee como un error del informe.
  avisoSustitucion: {
    'PREVISTO NO USADO': 'IN BOM, NOT USED',
    'USADO NO PREVISTO': 'USED, NOT IN BOM',
  },
  // Los rótulos reales de D365 en inglés, no una traducción propia.
  prodEstado: {
    Creado: 'Created',
    Estimado: 'Estimated',
    Programado: 'Scheduled',
    Liberado: 'Released',
    Iniciado: 'Started',
    'Notificado como terminado': 'Reported as finished',
    Terminado: 'Ended',
  },
}

/** Las columnas con vocabulario propio. El resto de valores no se toca. */
export const CAMPOS_VOCABULARIO = [...Object.keys(LEGADO), 'batchNo'] as const

const SIN_PEDIDO = ' (SIN PEDIDO)'

/**
 * El lote lleva el vocabulario DENTRO del dato, así que no cabe en un mapa:
 * `SIN LOTE` es un valor entero, pero `(SIN PEDIDO)` es un sufijo pegado a un
 * número de lote real que no se puede tocar.
 */
function normalizarLote(valor: string): string {
  if (valor === 'SIN LOTE') return 'NO BATCH'
  if (valor.endsWith(SIN_PEDIDO)) return valor.slice(0, -SIN_PEDIDO.length) + ' (NO PO)'
  return valor
}

/**
 * Un valor al vocabulario nuevo. Lo que ya viene en inglés pasa intacto, que es
 * lo que convierte este fichero en no-op cuando las vistas se despliegan.
 */
export function normalizarValor(campo: string, valor: string): string {
  if (valor === '') return valor
  if (campo === 'batchNo') return normalizarLote(valor)
  return LEGADO[campo]?.[valor] ?? valor
}

/**
 * Las filas de un informe, con su vocabulario al día.
 *
 * Devuelve las MISMAS filas cuando no hay nada que traducir —y así será cuando
 * las vistas estén desplegadas—, para no romper la identidad de referencia de la
 * que dependen los `useMemo` que cuelgan de las filas.
 */
export function normalizarFilas<T extends AnyReportRow>(rows: T[]): T[] {
  let algunCambio = false
  const salida = rows.map(row => {
    const fila = row as unknown as Record<string, unknown>
    let copia: Record<string, unknown> | null = null
    for (const campo of CAMPOS_VOCABULARIO) {
      const valor = fila[campo]
      if (typeof valor !== 'string') continue
      const nuevo = normalizarValor(campo, valor)
      if (nuevo === valor) continue
      copia ??= { ...fila }
      copia[campo] = nuevo
    }
    if (!copia) return row
    algunCambio = true
    return copia as unknown as T
  })
  return algunCambio ? salida : rows
}
